/**
 * FP Monthly COGS Tracker - admin follow-up dashboard.
 *
 * Backed by the `cogs_completion_monthly` platform-report job. Same
 * URL slug (`/admin/platform-reports/cogs-completion-monthly`); the
 * row shape, renderer, and KPI strip are tuned for at-a-glance
 * follow-up rather than wide-spreadsheet review.
 */
import React from 'react'
import { Alert, Box, LinearProgress, Typography } from '@mui/material'
import AdminPageHeader from '../../../../../components/admin/shared/AdminPageHeader'
import useReportJob from '../../../../../hooks/useReportJob'
import { platformReportsService } from '../../../../../services/platformReportsService'
import CogsTrackerFilters from './CogsTrackerFilters'
import CogsTrackerKpiRow from './CogsTrackerKpiRow'
import CogsTrackerGrid from './CogsTrackerGrid'
import type {
  CogsTrackerFilters as CogsTrackerFiltersType,
  CogsTrackerKpis,
  CogsTrackerRow,
} from './types'

const REPORT_TYPE = 'cogs_completion_monthly'
const REPORT_NAME = 'fp_monthly_cogs_tracker'
const PAGE_TITLE = 'FP Monthly COGS Tracker'
const PAGE_DESCRIPTION =
  "Track monthly COGS workbook progress, follow-up items, and tool migration status across franchise partners."

const monthKey = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`

const defaultFilters = (): CogsTrackerFiltersType => {
  const now = new Date()
  const end = new Date(now.getFullYear(), now.getMonth(), 1)
  const start = new Date(end.getFullYear(), end.getMonth() - 11, 1)
  return { start_month: monthKey(start), end_month: monthKey(end) }
}

const CogsTrackerPage: React.FC = () => {
  const { job, submit, isPolling } = useReportJob(REPORT_TYPE)
  const [filters, setFilters] = React.useState<CogsTrackerFiltersType>(
    defaultFilters,
  )
  const [submitted, setSubmitted] = React.useState(false)

  React.useEffect(() => {
    if (!submitted) {
      submit({ ...filters } as Record<string, unknown>)
      setSubmitted(true)
    }
  }, [submit, submitted, filters])

  const handleFilterChange = (
    key: keyof CogsTrackerFiltersType,
    value: string | undefined,
  ) => {
    setFilters((prev) => ({ ...prev, [key]: value }))
  }

  const handleClear = () => {
    const reset = defaultFilters()
    setFilters(reset)
    submit({ ...reset } as Record<string, unknown>)
  }

  const handleRun = () => {
    submit({ ...filters } as Record<string, unknown>)
  }

  const handleExport = async () => {
    if (!job?.job_id) return
    await platformReportsService.downloadJob(job.job_id, REPORT_NAME)
  }

  const status = job?.status
  const isLoading = isPolling || status === 'pending' || status === 'running'

  const rows = (job?.result_data || []) as unknown as CogsTrackerRow[]
  const summary = (job?.result_summary || {}) as Record<string, unknown>
  const kpis = (summary._kpi as CogsTrackerKpis | undefined) || null
  const windowMonths = kpis?.window_months || []

  return (
    <Box>
      <AdminPageHeader
        pageKey="platformReportDetail"
        titleOverride={PAGE_TITLE}
        descriptionOverride={PAGE_DESCRIPTION}
        breadcrumbs={[
          { label: 'Admin', to: '/admin' },
          { label: 'Platform Reports', to: '/admin/platform-reports' },
          { label: PAGE_TITLE },
        ]}
      />

      <CogsTrackerFilters
        filters={filters}
        onChange={handleFilterChange}
        onClear={handleClear}
        onRun={handleRun}
        onExport={handleExport}
        isLoading={isLoading}
        canExport={status === 'completed' && (job?.result_data?.length || 0) > 0}
      />

      <CogsTrackerKpiRow kpis={kpis} rows={rows} loading={isLoading} />

      {isLoading ? (
        <Box sx={{ mb: 2 }}>
          <LinearProgress />
          <Typography variant="caption" color="text.secondary">
            {job?.progress_message || 'Loading...'}
          </Typography>
        </Box>
      ) : null}

      {status === 'failed' ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {job?.error_message || 'Report failed to run.'}
        </Alert>
      ) : null}

      {status === 'completed' ? (
        <CogsTrackerGrid rows={rows} windowMonths={windowMonths} />
      ) : null}
    </Box>
  )
}

export default CogsTrackerPage
